/**
 * RolesRightsTab Component
 *
 * Tab for managing user roles and viewing computed rights/permissions.
 * Features: assign/remove roles, view all permissions.
 */

import { useState, useMemo } from 'react';
import { IconPlus, IconTrash, IconShield, IconKey, IconAlertCircle } from '@tabler/icons-react';
import {
  Card,
  Stack,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  Alert,
  ScrollArea,
  Text,
  MultiSelect,
  EmptyState,
} from '../../../../components/ui';
import { useUserManagement } from '../hooks/useUserManagement';
import type { RolesRightsTabProps, AssignRolesViewModel } from '../types';

export function RolesRightsTab({
  userId,
  currentRoles,
  availableRoles,
  allRights,
  onRolesUpdate,
  loading = false,
}: RolesRightsTabProps) {
  const userManagement = useUserManagement();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Available role options (excluding current roles)
  const availableRoleOptions = useMemo(() => {
    return availableRoles
      .filter((role) => !currentRoles.includes(role.name!))
      .map((role) => ({
        value: role.name!,
        label: role.name!,
      }));
  }, [availableRoles, currentRoles]);

  /**
   * Assign roles to user
   */
  const handleAssignRoles = async () => {
    if (selectedRoles.length === 0) return;

    setSubmitError(null);

    try {
      const assignData: AssignRolesViewModel = {
        roleNames: [...currentRoles, ...selectedRoles],
      };

      await userManagement.assignRoles(userId, assignData);
      await onRolesUpdate();
      setSelectedRoles([]);
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to assign roles');
    }
  };

  /**
   * Remove role from user
   */
  const handleRemoveRole = async (roleToRemove: string) => {
    // Prevent removing all roles
    if (currentRoles.length === 1) {
      setSubmitError('User must have at least one role');
      return;
    }

    setSubmitError(null);

    try {
      const assignData: AssignRolesViewModel = {
        roleNames: currentRoles.filter((r) => r !== roleToRemove),
      };

      await userManagement.assignRoles(userId, assignData);
      await onRolesUpdate();
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to remove role');
    }
  };

  return (
    <Stack gap="md">
      {/* Error Alert */}
      {submitError && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          title="Error"
          withCloseButton
          onClose={() => setSubmitError(null)}
        >
          {submitError}
        </Alert>
      )}

      {/* Roles Section */}
      <Card withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                Assigned Roles
              </Text>
              <Text size="xs" c="dimmed">
                Manage user role assignments
              </Text>
            </div>
            <Badge size="lg" variant="light" color="blue">
              {currentRoles.length} {currentRoles.length === 1 ? 'Role' : 'Roles'}
            </Badge>
          </Group>

          {/* Current Roles Table */}
          {currentRoles.length > 0 ? (
            <ScrollArea>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Role Name</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th width={100}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {currentRoles.map((roleName) => {
                    const roleDetails = availableRoles.find((r) => r.name === roleName);
                    return (
                      <Table.Tr key={roleName}>
                        <Table.Td>
                          <Group gap="xs">
                            <IconShield size={16} />
                            <Text fw={500}>{roleName}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {roleDetails?.description || 'No description'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleRemoveRole(roleName)}
                            disabled={userManagement.loading || currentRoles.length === 1}
                            title={currentRoles.length === 1 ? 'User must have at least one role' : 'Remove role'}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          ) : (
            <EmptyState
              icon={<IconShield size={48} />}
              title="No roles assigned"
              description="This user has no roles assigned yet."
              size="sm"
            />
          )}

          {/* Add Roles Section */}
          {availableRoleOptions.length > 0 && (
            <>
              <Text size="sm" fw={500} mt="md">
                Add Roles
              </Text>
              <Group gap="md" align="flex-end">
                <div style={{ flex: 1 }}>
                  <MultiSelect
                    placeholder="Select roles to add"
                    data={availableRoleOptions}
                    value={selectedRoles}
                    onChange={setSelectedRoles}
                    disabled={userManagement.loading}
                    searchable
                    clearable
                  />
                </div>
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={handleAssignRoles}
                  disabled={selectedRoles.length === 0 || userManagement.loading}
                  loading={userManagement.loading}
                >
                  Add Selected
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Card>

      {/* Rights/Permissions Section */}
      <Card withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                Computed Permissions
              </Text>
              <Text size="xs" c="dimmed">
                All permissions granted through assigned roles
              </Text>
            </div>
            <Badge size="lg" variant="light" color="cyan">
              {allRights.length} {allRights.length === 1 ? 'Permission' : 'Permissions'}
            </Badge>
          </Group>

          {/* Rights Table */}
          {allRights.length > 0 ? (
            <ScrollArea>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Permission Name</Table.Th>
                    <Table.Th>Description</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {allRights.map((right) => (
                    <Table.Tr key={right.id}>
                      <Table.Td>
                        <Group gap="xs">
                          <IconKey size={16} />
                          <Text fw={500}>{right.name}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {right.description || 'No description'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          ) : (
            <EmptyState
              icon={<IconKey size={48} />}
              title="No permissions"
              description="This user has no permissions assigned through roles."
              size="sm"
            />
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
